import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { AlertTriangle, Lightbulb, Zap, Volume2 } from "lucide-react";

const Pumps = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Pumps & Automation" 
        description="Variable speed pumps, programming, and automation systems"
      >
        <section className="space-y-8">
          {/* Variable Speed Benefits */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Variable Speed Advantage
            </h2>
            <p className="text-muted-foreground mb-4">
              Most BlueLux clients have variable speed pumps. These are far more efficient than single-speed 
              pumps and offer significant benefits when programmed correctly.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-background rounded-lg p-4 border">
                <h4 className="font-semibold text-foreground mb-1">Energy Savings</h4>
                <p className="text-sm text-muted-foreground">Up to 80% reduction in energy cost vs single-speed</p>
              </div>
              <div className="bg-background rounded-lg p-4 border">
                <h4 className="font-semibold text-foreground mb-1">Quieter Operation</h4>
                <p className="text-sm text-muted-foreground">Low speeds are nearly silent — clients appreciate this</p>
              </div>
              <div className="bg-background rounded-lg p-4 border">
                <h4 className="font-semibold text-foreground mb-1">Better Filtration</h4>
                <p className="text-sm text-muted-foreground">Slower flow = more contact time in the filter</p>
              </div>
            </div>
          </div>

          {/* Common Systems */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Common Systems</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Pentair IntelliFlo</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Most common in our service area</li>
                  <li>• 8 programmable speed settings</li>
                  <li>• Built-in timer or automation control</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Hayward VS Omni</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• OmniLogic integration</li>
                  <li>• Rotary dial speed control</li>
                  <li>• Self-diagnostic features</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Jandy ePump</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• iAquaLink compatible</li>
                  <li>• FloPro VS models</li>
                  <li>• Simple programming interface</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Speed Settings */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Speed Recommendations</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="border p-3 text-left font-semibold">Purpose</th>
                    <th className="border p-3 text-left font-semibold">RPM Range</th>
                    <th className="border p-3 text-left font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr>
                    <td className="border p-3 font-medium text-foreground">Daily Filtration</td>
                    <td className="border p-3">1000-1500 RPM</td>
                    <td className="border p-3">Low energy, quiet, adequate flow</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border p-3 font-medium text-foreground">Skimmer Operation</td>
                    <td className="border p-3">1500-2000 RPM</td>
                    <td className="border p-3">Enough pull for surface debris</td>
                  </tr>
                  <tr>
                    <td className="border p-3 font-medium text-foreground">Salt Cell Production</td>
                    <td className="border p-3">2000-2500 RPM</td>
                    <td className="border p-3">Adequate flow for cell operation</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border p-3 font-medium text-foreground">Cleaning/Vacuuming</td>
                    <td className="border p-3">2500-3000 RPM</td>
                    <td className="border p-3">Higher suction for debris pickup</td>
                  </tr>
                  <tr>
                    <td className="border p-3 font-medium text-foreground">Spa Jets/Water Features</td>
                    <td className="border p-3">3000+ RPM</td>
                    <td className="border p-3">High flow, use only when needed</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> Running at 1500 RPM for 12 hours 
                  moves more water and uses less energy than running at 3000 RPM for 4 hours. Lower and 
                  longer is almost always better.
                </p>
              </div>
            </div>
          </div>

          {/* Priming */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Priming the Pump</h2>
            <p className="text-muted-foreground mb-4">
              If the pump loses prime (air in the system), it needs to be manually primed to restart 
              proper circulation.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-4">Priming Procedure</h4>
              <ol className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">1</span>
                  <span>Turn off pump at breaker</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">2</span>
                  <span>Open pump lid, fill strainer housing with water from hose</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">3</span>
                  <span>Replace lid, ensure O-ring is lubricated and seated</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">4</span>
                  <span>Open air relief valve on filter</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">5</span>
                  <span>Turn on pump — watch for water in strainer</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">6</span>
                  <span>Close air relief when water sprays steadily</span>
                </li>
              </ol>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Caution:</strong> If pump won't prime after 2-3 attempts, 
                  look for suction-side air leak (pump lid, unions, valves) before continuing to try.
                </p>
              </div>
            </div>
          </div>

          {/* Automation Basics */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Automation Systems</h2>
            <p className="text-muted-foreground mb-4">
              Most premium pools have automation systems that control the pump, heater, lights, valves, 
              and other equipment from a central panel or app.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Pentair IntelliCenter</h4>
                <p className="text-sm text-muted-foreground">
                  Touchscreen panel, ScreenLogic app, schedules for all equipment
                </p>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Hayward OmniLogic</h4>
                <p className="text-sm text-muted-foreground">
                  Modern interface, OmniHub app, voice control compatible
                </p>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Jandy iAquaLink</h4>
                <p className="text-sm text-muted-foreground">
                  AquaLink systems, mobile app control, reliable and common
                </p>
              </div>
            </div>
            <div className="bg-card border rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-foreground mb-2">Key Features to Know</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• <strong>Pool/Spa modes:</strong> Switch valves for different operation modes</li>
                <li>• <strong>Schedules:</strong> Automatic on/off times for equipment</li>
                <li>• <strong>Speed settings:</strong> Assign RPM to different features</li>
                <li>• <strong>Freeze protection:</strong> Auto-runs pump in cold weather</li>
              </ul>
            </div>
          </div>

          {/* Troubleshooting */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Volume2 className="h-6 w-6 text-primary" />
              Troubleshooting
            </h2>
            <div className="space-y-4">
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Air Bubbles in Pump</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Check pump lid O-ring — may be dry or damaged</li>
                  <li>• Inspect unions on suction side</li>
                  <li>• Verify water level in pool</li>
                  <li>• Check valve stem O-rings</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Pump Running but No/Low Flow</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Clogged baskets (skimmer or pump)</li>
                  <li>• Dirty filter (check pressure gauge)</li>
                  <li>• Closed or partially closed valve</li>
                  <li>• Clogged impeller (remove and inspect)</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Thermal Shutdown</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Pump overheating due to restricted flow</li>
                  <li>• Running dry (lost prime)</li>
                  <li>• Allow to cool, then check for flow restriction</li>
                  <li>• May indicate failing motor if frequent</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Unusual Noise</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• <strong>Growling/cavitation:</strong> Starving for water (clogged or low level)</li>
                  <li>• <strong>Grinding:</strong> Bearings failing</li>
                  <li>• <strong>Screaming/high pitch:</strong> Impeller issue</li>
                  <li>• <strong>Vibration:</strong> Loose mounting or unbalanced motor</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Error Codes */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Common Error Codes</h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-sm text-muted-foreground mb-4">
                Error codes vary by manufacturer. Here are common ones to recognize:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong className="text-foreground">PWR (Pentair):</strong> Power issue — check breaker</li>
                <li>• <strong className="text-foreground">OVR/OVER (Pentair):</strong> Overload — check for blockage</li>
                <li>• <strong className="text-foreground">DRV (Various):</strong> Drive failure — may need tech service</li>
                <li>• <strong className="text-foreground">Priming mode:</strong> Pump is trying to prime — may need help</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4">
                <strong>Note:</strong> Document any error codes in the app and notify the office if persistent.
              </p>
            </div>
          </div>

          {/* Why It Matters */}
          <div className="bg-card border-2 border-primary rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-3">Why This Matters</h3>
            <p className="text-muted-foreground">
              The pump is the heart of the system. When it's not running properly, nothing else works — 
              filtration stops, chemicals don't circulate, and water quality degrades quickly. Knowing 
              how to prime, troubleshoot, and optimize these systems is essential for every visit.
            </p>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Pumps;